<template>
  <div class="contents">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">Pendapatan</h4>
            </div>
            <div class="action-btn">
              <button @click="loadAll" class="btn btn-secondary text-white" style="margin: 5px">
                <i class="fas fa-sync"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Period Filter -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="card p-3">
            <div class="d-flex align-items-center" style="gap: 8px; flex-wrap: wrap">
              <span class="font-weight-bold">Periode:</span>
              <button v-for="p in periods" :key="p.value"
                @click="setPeriod(p.value)"
                :class="['btn btn-sm', period === p.value && !useCustomRange ? 'btn-primary' : 'btn-outline-primary']">
                {{ p.label }}
              </button>
              <span class="font-weight-bold" style="margin-left: 16px;">| Custom:</span>
              <input type="date" v-model="customRange.from" id="date_from_picker"
                class="form-control form-control-sm" style="width: 145px;" placeholder="Dari">
              <span>—</span>
              <input type="date" v-model="customRange.to"
                class="form-control form-control-sm" style="width: 145px;" placeholder="Sampai">
              <button @click="applyCustomRange"
                :class="['btn btn-sm', useCustomRange ? 'btn-success' : 'btn-outline-success']"
                :disabled="!customRange.from && !customRange.to">
                <i class="fas fa-filter"></i> Apply
              </button>
              <button v-if="useCustomRange" @click="clearCustomRange" class="btn btn-sm btn-outline-secondary">
                <i class="fas fa-times"></i> Clear
              </button>
              <span v-if="stats" class="text-muted small ml-auto">
                Sejak: <b>{{ formatDateRange() }}</b>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Info Alert -->
      <div class="alert alert-info mb-3" v-if="stats">
        <small>
          <i class="fas fa-info-circle"></i>
          <b>Cara hitung:</b> Pendapatan REAL = <b>top up saldo</b> + <b>beli paket langsung</b> (StarQRIS/QR ke paket) + <b>order publik</b>.
          Beli paket dari saldo (<i>pay</i>) tidak dihitung lagi — itu pemakaian saldo yang sudah pernah masuk lewat top up.
        </small>
      </div>

      <!-- Stats Cards Row 1: Revenue Real -->
      <div class="row" v-if="stats">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #1976d2">
            <div class="text-muted small">💰 TOTAL PENDAPATAN (UANG MASUK)</div>
            <div class="h4 mb-0 font-weight-bold text-primary">{{ formatRupiah(stats.total_revenue) }}</div>
            <small class="text-muted">{{ stats.total_transaction }} transaksi (top up + beli langsung + order)</small>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #d84315">
            <div class="text-muted small">⬇️ PEMBAYARAN KELUAR</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#d84315">{{ formatRupiah(stats.total_payout) }}</div>
            <small class="text-muted">withdraw user (saldo referral, dll)</small>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #2e7d32">
            <div class="text-muted small">✅ NET REVENUE</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#2e7d32">{{ formatRupiah(stats.net_revenue) }}</div>
            <small class="text-muted">uang masuk dikurangi withdraw</small>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #f9a825">
            <div class="text-muted small">⏳ PENDING TOPUP</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#f9a825">{{ formatRupiah(stats.pending_amount) }}</div>
            <small class="text-muted">{{ stats.pending_count }} potensi inflow</small>
          </div>
        </div>
      </div>

      <!-- Stats Cards Row 2: Saldo Info -->
      <div class="row" v-if="stats">
        <div class="col-md-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #6c757d; background:#f8fafd">
            <div class="text-muted small">💼 SALDO USER (BELUM TERPAKAI) <i class="fas fa-question-circle" title="Total saldo aktif di semua akun user yang belum dipakai untuk beli paket. Lifetime, tidak filter periode."></i></div>
            <div class="h4 mb-0 font-weight-bold" style="color:#455a64">{{ formatRupiah(stats.active_balance) }}</div>
            <small class="text-muted">Saldo ngendap di akun user — sudah masuk revenue tapi belum dibelanjakan</small>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #9c27b0; background:#faf6fd">
            <div class="text-muted small">🛒 BELI PAKET DARI SALDO <i class="fas fa-question-circle" title="Transaksi pay/pay_referral — pakai saldo user, bukan pembayaran gateway baru."></i></div>
            <div class="h4 mb-0 font-weight-bold" style="color:#7b1fa2">{{ formatRupiah(stats.saldo_used_amount) }}</div>
            <small class="text-muted">{{ stats.saldo_used_count }} transaksi pay — info saja, sudah masuk lewat top up</small>
          </div>
        </div>
      </div>

      <!-- Breakdown by Mode + Method -->
      <div class="row" v-if="stats">
        <div class="col-md-6 mb-3">
          <div class="card p-3">
            <h6 class="font-weight-bold"><i class="fas fa-tags text-primary"></i> Breakdown Sumber Pendapatan</h6>
            <table class="table table-sm mt-2" v-if="stats.by_mode && stats.by_mode.length">
              <thead><tr><th>Sumber</th><th class="text-right">Jumlah</th><th class="text-right">Total</th></tr></thead>
              <tbody>
                <tr v-for="m in stats.by_mode" :key="m.mode">
                  <td><span :class="modeBadgeClass(m.mode)">{{ modeLabel(m.mode) }}</span></td>
                  <td class="text-right">{{ m.count }}</td>
                  <td class="text-right font-weight-bold">{{ formatRupiah(m.total) }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="text-muted">Tidak ada uang masuk di periode ini.</p>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <div class="card p-3">
            <h6 class="font-weight-bold"><i class="fas fa-credit-card text-success"></i> Top Metode Pembayaran</h6>
            <table class="table table-sm mt-2" v-if="stats.by_method && stats.by_method.length">
              <thead><tr><th>Metode</th><th class="text-right">Jumlah</th><th class="text-right">Total</th></tr></thead>
              <tbody>
                <tr v-for="m in stats.by_method" :key="m.method">
                  <td>{{ m.method }}</td>
                  <td class="text-right">{{ m.count }}</td>
                  <td class="text-right font-weight-bold">{{ formatRupiah(m.total) }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="text-muted">Tidak ada data.</p>
          </div>
        </div>
      </div>

      <!-- Chart 12 bulan -->
      <div class="row" v-if="stats && stats.chart_12months && stats.chart_12months.length">
        <div class="col-12 mb-3">
          <div class="card p-3">
            <h6 class="font-weight-bold mb-3"><i class="fas fa-chart-line text-info"></i> Trend 12 Bulan Terakhir</h6>
            <div class="chart-bars">
              <div v-for="(c, idx) in stats.chart_12months" :key="idx" class="chart-bar-item">
                <div class="bar-wrapper">
                  <div class="bar-fill"
                    :style="{ height: barHeight(c.total) + '%' }"
                    :title="formatRupiah(c.total) + ' (' + c.count + ' tx)'">
                  </div>
                </div>
                <div class="bar-label">{{ formatMonth(c.month) }}</div>
                <div class="bar-value">{{ formatRupiahShort(c.total) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pembelian / aktivasi paket -->
      <div class="row" v-if="stats">
        <div class="col-12 mb-3">
          <div class="card p-3">
            <h6 class="font-weight-bold mb-2"><i class="fas fa-box text-purple"></i> Paket dibeli / diaktifkan (sesuai periode)</h6>
            <p class="text-muted small mb-2">
              Termasuk beli paket via saldo (<code>pay</code>), order publik (<code>public_order</code>), dan top up langsung ke paket (StarQRIS / billing).
            </p>
            <div v-if="packageLoading" class="text-center p-3"><div class="spinner-border spinner-border-sm"></div></div>
            <div v-else-if="!packageRows.length" class="text-muted small">Tidak ada aktivasi paket di periode ini.</div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th>Waktu</th>
                    <th>User</th>
                    <th>Paket</th>
                    <th>Masa aktif</th>
                    <th>Sumber</th>
                    <th class="text-right">Nominal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in packageRows" :key="'pkg-' + t.id">
                    <td><small>{{ formatTime(t.created_at) }}</small></td>
                    <td><small>{{ t.user_email || '-' }}<br><span class="text-muted">{{ t.user_phone || '' }}</span></small></td>
                    <td><small>{{ offerPackageLabel(t) }}</small></td>
                    <td><small>{{ formatExpiryCell(t) }}</small></td>
                    <td><span :class="modeBadgeClass(t.mode)">{{ modeLabel(t.mode) }}</span> <small class="text-muted">{{ t.method || '' }}</small></td>
                    <td class="text-right font-weight-bold">{{ formatRupiah(t.nominal) }}</td>
                    <td class="text-nowrap">
                      <button
                        v-if="t.user_is_expired && t.user_id && !t.offer_email_sent"
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        :disabled="offerSendingId === t.user_id"
                        @click="openOfferModal(t)"
                      >
                        Kirim penawaran
                      </button>
                      <small v-else-if="t.offer_email_sent" class="text-muted">Penawaran terkirim</small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!packageLoading && packageRows.length" class="d-flex justify-content-between align-items-center pt-2">
              <small class="text-muted">{{ packageTotal }} aktivasi paket — halaman {{ packagePage }} / {{ packageTotalPages }}</small>
              <div class="btn-group">
                <button class="btn btn-sm btn-outline-primary" @click="loadPackageList(packagePage - 1)" :disabled="packagePage <= 1">←</button>
                <button class="btn btn-sm btn-outline-primary" @click="loadPackageList(packagePage + 1)" :disabled="packagePage >= packageTotalPages">→</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filter Transaksi -->
      <div class="row">
        <div class="col-12 mb-2">
          <div class="card p-3">
            <h6 class="font-weight-bold mb-3"><i class="fas fa-filter"></i> Daftar Transaksi (semua mode)</h6>
            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Status</label>
                <select v-model="filter.status" @change="loadList(1)" class="form-control form-control-sm">
                  <option value="">Semua</option>
                  <option value="success">Sukses</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Gagal</option>
                </select>
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Mode</label>
                <select v-model="filter.mode" @change="loadList(1)" class="form-control form-control-sm">
                  <option value="">Semua</option>
                  <option value="pay">Beli Paket</option>
                  <option value="pay_referral">Beli via Referral</option>
                  <option value="topup">Top Up Saldo</option>
                  <option value="topup chatgpt">Top Up ChatGPT</option>
                  <option value="public_order">Order / StarQRIS (paket)</option>
                  <option value="withdraw">Withdraw</option>
                </select>
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Cari Email/HP</label>
                <input v-model="filter.q" @input="onSearch" type="text" class="form-control form-control-sm" placeholder="email atau no hp" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Per Page</label>
                <select v-model.number="filter.limit" @change="loadList(1)" class="form-control form-control-sm">
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Dari Tanggal</label>
                <input v-model="filter.date_from" @change="loadList(1)" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Sampai Tanggal</label>
                <input v-model="filter.date_to" @change="loadList(1)" type="date" class="form-control form-control-sm" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-12">
          <div class="card global-shadow border bg-white radius-xl w-100 mb-30">
            <div v-if="isLoading" class="text-center p-4">
              <div class="spinner-border" role="status"></div>
              <p class="mt-2 text-muted">Loading...</p>
            </div>
            <div v-else-if="!transactions.length" class="text-center text-muted p-4">
              <p>Tidak ada transaksi.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th>Waktu</th>
                    <th>User</th>
                    <th>Mode</th>
                    <th>Method</th>
                    <th class="text-right">Nominal</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in transactions" :key="t.id">
                    <td><small>{{ formatTime(t.created_at) }}</small></td>
                    <td>
                      <div><small style="font-family:monospace">{{ t.user_email || '-' }}</small></div>
                      <small class="text-muted">{{ t.user_phone || '-' }} (#{{ t.user_id }})</small>
                    </td>
                    <td><span :class="modeBadgeClass(t.mode)">{{ modeLabel(t.mode) }}</span></td>
                    <td><small>{{ t.method || '-' }}</small></td>
                    <td class="text-right font-weight-bold">{{ formatRupiah(t.nominal) }}</td>
                    <td>
                      <span v-if="t.status === 'success'" class="badge badge-success">Sukses</span>
                      <span v-else-if="t.status === 'pending'" class="badge badge-warning text-white">Pending</span>
                      <span v-else-if="t.status === 'failed'" class="badge badge-danger">Gagal</span>
                      <span v-else class="badge badge-secondary">{{ t.status }}</span>
                    </td>
                    <td><small class="text-muted" :title="t.metadata">{{ truncate(t.metadata, 40) }}</small></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Pagination -->
            <div v-if="!isLoading && transactions.length" class="d-flex justify-content-between align-items-center p-3">
              <small class="text-muted">
                Menampilkan {{ ((page - 1) * filter.limit) + 1 }} - {{ Math.min(page * filter.limit, total) }} dari {{ total }} transaksi
              </small>
              <div class="btn-group">
                <button class="btn btn-sm btn-outline-primary" @click="loadList(page - 1)" :disabled="page <= 1">← Prev</button>
                <button class="btn btn-sm btn-outline-primary disabled">Page {{ page }} / {{ totalPages }}</button>
                <button class="btn btn-sm btn-outline-primary" @click="loadList(page + 1)" :disabled="page >= totalPages">Next →</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal penawaran perpanjangan -->
      <div v-if="offerModal.open" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,0.45)">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Penawaran perpanjangan paket</h5>
              <button type="button" class="close" @click="closeOfferModal"><span>&times;</span></button>
            </div>
            <div class="modal-body">
              <p class="small text-muted mb-2">
                User: <b>{{ offerModal.userEmail }}</b>
                <span v-if="offerModal.packageName"> · Paket: <b>{{ offerModal.packageName }}</b></span>
              </p>
              <p v-if="offerModal.loading" class="text-muted">Memuat preview…</p>
              <template v-else>
                <label class="small font-weight-bold">Subject</label>
                <input v-model="offerModal.subject" class="form-control form-control-sm mb-2" readonly />
                <label class="small font-weight-bold">Isi email (HTML)</label>
                <div class="border rounded p-2 mb-2" style="max-height:220px;overflow:auto;background:#fff" v-html="offerModal.html"></div>
                <textarea v-model="offerModal.html" class="form-control form-control-sm" rows="6" readonly style="font-size:11px"></textarea>
              </template>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeOfferModal">Tutup</button>
              <button
                type="button"
                class="btn btn-outline-info"
                :disabled="offerModal.loading || offerSending"
                @click="sendOffer(true)"
              >
                Test ke starfield.id@gmail.com
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="offerModal.loading || offerSending"
                @click="sendOffer(false)"
              >
                Kirim ke user
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      stats: null,
      transactions: [],
      total: 0,
      totalPages: 1,
      page: 1,
      isLoading: false,
      period: "month",
      useCustomRange: false,
      customRange: { from: "", to: "" },
      periods: [
        { value: "today", label: "Hari Ini" },
        { value: "week", label: "7 Hari" },
        { value: "month", label: "30 Hari" },
        { value: "year", label: "1 Tahun" },
        { value: "all", label: "Lifetime" },
      ],
      filter: {
        status: "success",
        mode: "",
        method: "",
        q: "",
        date_from: "",
        date_to: "",
        limit: 25,
      },
      packageRows: [],
      packageTotal: 0,
      packagePage: 1,
      packageTotalPages: 1,
      packageLoading: false,
      searchDebounce: null,
      offerSending: false,
      offerSendingId: 0,
      offerModal: {
        open: false,
        loading: false,
        userId: 0,
        packageId: 0,
        userEmail: "",
        packageName: "",
        subject: "",
        html: "",
      },
    };
  },
  computed: {
    maxBarValue() {
      if (!this.stats || !this.stats.chart_12months) return 1;
      const max = Math.max(...this.stats.chart_12months.map(c => c.total));
      return max || 1;
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    setPeriod(p) {
      this.period = p;
      this.useCustomRange = false;
      this.customRange = { from: "", to: "" };
      this.syncPeriodToFilter();
      this.loadAll();
    },
    applyCustomRange() {
      if (!this.customRange.from && !this.customRange.to) return;
      this.useCustomRange = true;
      this.filter.date_from = this.customRange.from;
      this.filter.date_to = this.customRange.to;
      this.loadAll();
    },
    clearCustomRange() {
      this.useCustomRange = false;
      this.customRange = { from: "", to: "" };
      this.syncPeriodToFilter();
      this.loadAll();
    },
    syncPeriodToFilter() {
      if (this.useCustomRange) return;
      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };
      const now = new Date();
      if (this.period === "all") {
        this.filter.date_from = "";
        this.filter.date_to = "";
        return;
      }
      let from = new Date(now);
      if (this.period === "today") {
        /* same day */
      } else if (this.period === "week") {
        from.setDate(from.getDate() - 7);
      } else if (this.period === "month") {
        from.setDate(from.getDate() - 30);
      } else if (this.period === "year") {
        from.setFullYear(from.getFullYear() - 1);
      }
      this.filter.date_from = fmt(from);
      this.filter.date_to = fmt(now);
    },
    barHeight(val) {
      return Math.max(2, (val / this.maxBarValue) * 100);
    },
    formatRupiah(n) {
      const num = parseInt(n) || 0;
      return "Rp " + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatRupiahShort(n) {
      const num = parseInt(n) || 0;
      if (num >= 1e9) return (num/1e9).toFixed(1) + "M";
      if (num >= 1e6) return (num/1e6).toFixed(1) + "jt";
      if (num >= 1e3) return (num/1e3).toFixed(0) + "k";
      return num;
    },
    formatMonth(ym) {
      if (!ym) return "-";
      const [y, m] = ym.split("-");
      const monthNames = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Ags","Sep","Okt","Nov","Des"];
      return `${monthNames[parseInt(m)-1]} ${y.slice(2)}`;
    },
    formatTime(millis) {
      if (!millis) return "-";
      return new Date(parseInt(millis)).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit", month: "short", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },
    formatDateRange() {
      if (this.filter.date_from && this.filter.date_to) {
        return `${this.filter.date_from} — ${this.filter.date_to}`;
      }
      if (!this.stats || !this.stats.start_millis) return "Lifetime";
      return new Date(parseInt(this.stats.start_millis)).toLocaleDateString("id-ID", {
        timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric",
      });
    },
    packageLabel(t) {
      const raw = t.metadata || "";
      if (!raw) return "-";
      try {
        const m = JSON.parse(raw);
        if (m.package_name) return m.package_name;
        if (m.buy_package_id) return "Paket #" + m.buy_package_id;
      } catch (e) { /* noop */ }
      if (raw.includes("package_name")) return this.truncate(raw, 48);
      return "-";
    },
    offerPackageLabel(t) {
      if (t.offer_package_name) return t.offer_package_name;
      return this.packageLabel(t);
    },
    formatExpiryCell(t) {
      if (!t.user_expired_at) return "—";
      const exp = this.formatTime(t.user_expired_at);
      if (t.user_is_expired) return "Expired " + exp;
      const d = typeof t.days_until_expiry === "number" ? t.days_until_expiry : null;
      if (d !== null && d >= 0) return "Aktif s/d " + exp + " (" + d + " hari)";
      return "Aktif s/d " + exp;
    },
    closeOfferModal() {
      this.offerModal.open = false;
    },
    async openOfferModal(t) {
      this.offerModal = {
        open: true,
        loading: true,
        userId: t.user_id,
        packageId: t.offer_package_id || t.tx_package_id || t.user_package_id || 0,
        userEmail: t.user_email || "",
        packageName: t.offer_package_name || this.offerPackageLabel(t),
        subject: "",
        html: "",
      };
      try {
        const r = await axios.get("admin/revenue/package-offer-preview", {
          params: { user_id: this.offerModal.userId, package_id: this.offerModal.packageId || undefined },
        });
        const d = r.data.data || {};
        this.offerModal.subject = d.subject || "";
        this.offerModal.html = d.html || "";
        if (d.offer_package_name) this.offerModal.packageName = d.offer_package_name;
      } catch (err) {
        this.$toast.error("Gagal load preview: " + (err.response?.data?.message || err.message));
        this.closeOfferModal();
      } finally {
        this.offerModal.loading = false;
      }
    },
    async sendOffer(isTest) {
      if (!this.offerModal.userId) return;
      this.offerSending = true;
      this.offerSendingId = this.offerModal.userId;
      try {
        const body = {
          user_id: this.offerModal.userId,
          package_id: this.offerModal.packageId || 0,
        };
        if (isTest) body.send_to = "starfield.id@gmail.com";
        const r = await axios.post("admin/revenue/send-package-offer", body);
        this.$toast.success(r.data.message || "Email terkirim");
        if (!isTest) {
          this.closeOfferModal();
          await this.loadPackageList(this.packagePage);
        }
      } catch (err) {
        this.$toast.error(err.response?.data?.message || err.message);
      } finally {
        this.offerSending = false;
        this.offerSendingId = 0;
      }
    },
    truncate(s, n) {
      if (!s) return "-";
      return s.length > n ? s.substring(0, n) + "..." : s;
    },
    modeLabel(m) {
      return ({
        "pay": "Beli Paket",
        "pay_referral": "Beli (Referral)",
        "topup": "Top Up",
        "topup chatgpt": "Top Up ChatGPT",
        "withdraw": "Withdraw",
        "public_order": "Order publik",
        "direct_package": "Beli paket langsung (QR)",
      })[m] || m;
    },
    modeBadgeClass(m) {
      return ({
        "pay": "badge badge-primary",
        "pay_referral": "badge badge-info",
        "topup": "badge badge-success",
        "topup chatgpt": "badge badge-secondary",
        "withdraw": "badge badge-danger",
        "public_order": "badge badge-info",
        "direct_package": "badge badge-warning",
      })[m] || "badge badge-light";
    },
    onSearch() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.loadList(1), 500);
    },
    async loadAll() {
      if (!this.useCustomRange) this.syncPeriodToFilter();
      await Promise.all([this.loadStats(), this.loadList(1), this.loadPackageList(1)]);
    },
    async loadStats() {
      try {
        const params = {};
        if (this.useCustomRange && (this.customRange.from || this.customRange.to)) {
          if (this.customRange.from) params.date_from = this.customRange.from;
          if (this.customRange.to) params.date_to = this.customRange.to;
        } else {
          params.period = this.period;
        }
        const r = await axios.get("admin/revenue/stats", { params });
        this.stats = r.data.data;
      } catch (err) {
        this.$toast.error("Gagal load stats: " + (err.response?.data?.message || err.message));
      }
    },
    async loadPackageList(p) {
      if (p < 1) return;
      this.packageLoading = true;
      this.packagePage = p;
      try {
        const params = { page: p, limit: 25, package_only: "1", status: "success" };
        if (this.filter.date_from) params.date_from = this.filter.date_from;
        if (this.filter.date_to) params.date_to = this.filter.date_to;
        if (this.filter.q) params.q = this.filter.q;
        const r = await axios.get("admin/revenue/list", { params });
        this.packageRows = r.data.data.transactions || [];
        this.packageTotal = r.data.data.total || 0;
        this.packageTotalPages = r.data.data.total_pages || 1;
      } catch (err) {
        this.packageRows = [];
        this.packageTotal = 0;
      } finally {
        this.packageLoading = false;
      }
    },
    async loadList(p) {
      this.isLoading = true;
      this.page = p;
      try {
        const params = { page: p, limit: this.filter.limit };
        if (this.filter.status) params.status = this.filter.status;
        if (this.filter.mode) params.mode = this.filter.mode;
        if (this.filter.method) params.method = this.filter.method;
        if (this.filter.q) params.q = this.filter.q;
        if (this.filter.date_from) params.date_from = this.filter.date_from;
        if (this.filter.date_to) params.date_to = this.filter.date_to;
        const r = await axios.get("admin/revenue/list", { params });
        this.transactions = r.data.data.transactions || [];
        this.total = r.data.data.total || 0;
        this.totalPages = r.data.data.total_pages || 1;
      } catch (err) {
        this.$toast.error("Gagal load: " + (err.response?.data?.message || err.message));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  min-height: 200px;
  padding: 0 4px;
}
.chart-bar-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.bar-wrapper {
  width: 100%;
  height: 160px;
  display: flex;
  align-items: flex-end;
  background: linear-gradient(to bottom, transparent 0%, transparent 80%, #f0f4f8 100%);
  border-radius: 4px;
}
.bar-fill {
  width: 100%;
  background: linear-gradient(180deg, #1976d2 0%, #42a5f5 100%);
  border-radius: 4px 4px 0 0;
  transition: height 0.3s;
  cursor: pointer;
}
.bar-fill:hover {
  background: linear-gradient(180deg, #0d47a1 0%, #1976d2 100%);
}
.bar-label {
  font-size: 10px;
  color: #6c757d;
  margin-top: 4px;
}
.bar-value {
  font-size: 10px;
  color: #1976d2;
  font-weight: 600;
}
</style>
